import React, { useState } from 'react';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import StatusBadge from '../common/StatusBadge';
import { toast } from 'react-toastify';
import { financeOperatorAPI } from '../../services/api';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

const FinancePaymentTable = ({ payments, onRefresh, onApproval }) => {
  const { isDarkMode } = useAdminTheme();
  const [approving, setApproving] = useState({});

  const getSourceInfo = (payment) => {
    const sourceType = payment.dataSource?.type || 'manual';
    
    const sourceMap = {
      'whatsapp_business': { label: 'WHATSAPP', color: 'green' },
      'unstructured_email': { label: 'EMAIL-AI', color: 'yellow' },
      'structured_email': { label: 'EMAIL-STR', color: 'blue' },
      'admin': { label: 'ADMIN', color: 'purple' },
      'user_dashboard': { label: 'USER', color: 'indigo' },
      'manual': { label: 'MANUAL', color: 'gray' }
    };
    
    return sourceMap[sourceType] || sourceMap['manual'];
  };

  const handleApprove = async (paymentId) => {
    try {
      setApproving(prev => ({ ...prev, [paymentId]: true }));
      const response = await financeOperatorAPI.approvePayment(paymentId);

      if (response.success) {
        toast.success('Payment approved successfully');
        if (onApproval) onApproval();
        if (onRefresh) onRefresh();
      } else {
        toast.error(response.message || 'Failed to approve payment');
      }
    } catch (error) {
      console.error('Error approving payment:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to approve payment');
      }
    } finally {
      setApproving(prev => ({ ...prev, [paymentId]: false }));
    }
  };

  const getStripeDashboardUrl = (stripeSessionId) => {
    if (!stripeSessionId) return '#';
    // Stripe dashboard URL for checkout sessions
    return `https://dashboard.stripe.com/payments?checkout_session=${stripeSessionId}`;
  };

  return (
    <div className={isDarkMode ? 'card-dark overflow-hidden' : 'card overflow-hidden'}>
      <div className="overflow-x-auto">
        <table className={isDarkMode ? 'table-dark' : 'table'}>
          <thead className={isDarkMode ? 'table-header-dark' : 'table-header'}>
            <tr>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Client</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Source</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Service Type</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Amount</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Status</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Created</th>
              <th className={isDarkMode ? 'table-header-cell-dark' : 'table-header-cell'}>Actions</th>
            </tr>
          </thead>
          <tbody className={isDarkMode ? 'table-body-dark' : 'table-body'}>
            {payments.map((payment) => {
              const sourceInfo = getSourceInfo(payment);
              
              return (
                <tr key={payment._id} className={`transition-colors duration-300 ${
                  isDarkMode ? 'hover:bg-skyone-inset-dark' : 'hover:bg-gray-50'
                }`}>
                  {/* Client Info */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <div>
                      <div className={`font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-100' : 'text-gray-900'
                      }`}>
                        {payment.clientName}
                      </div>
                      <div className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {payment.clientEmail}
                      </div>
                    </div>
                  </td>

                  {/* Source */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                      sourceInfo.color === 'green'
                        ? (isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                        : sourceInfo.color === 'yellow'
                        ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-700' : 'bg-yellow-100 text-yellow-800')
                        : sourceInfo.color === 'blue'
                        ? (isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-800')
                        : sourceInfo.color === 'purple'
                        ? (isDarkMode ? 'bg-purple-900/40 text-purple-300 border border-purple-700' : 'bg-purple-100 text-purple-800')
                        : sourceInfo.color === 'indigo'
                        ? (isDarkMode ? 'bg-indigo-900/40 text-indigo-300 border border-indigo-700' : 'bg-indigo-100 text-indigo-800')
                        : (isDarkMode ? 'bg-skyone-inset-dark/90 text-gray-300 border border-skyone-border-dark' : 'bg-gray-100 text-gray-800')
                    }`}>
                      {sourceInfo.label}
                    </span>
                  </td>

                  {/* Service Type */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors duration-300 ${
                      payment.serviceType === 'ARRIVAL' 
                        ? (isDarkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-800')
                        : payment.serviceType === 'DEPARTURE' 
                          ? (isDarkMode ? 'bg-green-900/40 text-green-300 border border-green-700' : 'bg-green-100 text-green-800')
                          : (isDarkMode ? 'bg-yellow-900/40 text-yellow-300 border border-yellow-700' : 'bg-yellow-100 text-yellow-800')
                    }`}>
                      {payment.serviceType || 'ARRIVAL'}
                    </span>
                  </td>

                  {/* Amount */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <div className={`font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {formatCurrency(payment.amount, payment.currency)}
                    </div>
                  </td>

                  {/* Status */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <StatusBadge status="pending_approval" showIcon={true} />
                  </td>

                  {/* Created */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <div className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {formatDateTime(payment.createdAt)}
                    </div>
                  </td>

                  {/* Actions - Only Stripe Dashboard Link and Approve Button */}
                  <td className={isDarkMode ? 'table-cell-dark' : 'table-cell'}>
                    <div className="flex items-center space-x-2">
                      {/* Stripe Dashboard Link */}
                      <a
                        href={getStripeDashboardUrl(payment.stripeSessionId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center px-3 py-2 text-xs font-semibold border rounded-lg transition-all duration-200 shadow-sm ${
                          isDarkMode
                            ? 'text-blue-300 bg-blue-900/40 border-blue-700 hover:bg-blue-900/60 hover:text-blue-200'
                            : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200 hover:text-blue-800'
                        }`}
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Stripe
                      </a>

                      {/* Approve Button */}
                      <button
                        onClick={() => handleApprove(payment._id)}
                        disabled={approving[payment._id]}
                        className={`inline-flex items-center px-3 py-2 text-xs font-semibold rounded-lg transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                          isDarkMode
                            ? 'text-green-300 bg-green-900/40 border border-green-700 hover:bg-green-900/60 hover:text-green-200'
                            : 'text-green-700 bg-green-100 border border-green-300 hover:bg-green-200 hover:text-green-800'
                        }`}
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {approving[payment._id] ? 'Approving...' : 'Approve'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinancePaymentTable;

